import { StorageSerializers, useDocumentVisibility, useStorage, useTimeoutPoll } from '@vueuse/core'
import { defineStore } from 'pinia'
import { watch } from 'vue'
import { computed } from 'vue'

type Version = {
  timestamp: number
  hash: string
}

export const useVersionStore = defineStore('version', () => {
  const currentVersion = useStorage<Version | null>('kintsu-current-version', null, sessionStorage, {
    serializer: StorageSerializers.object,
  })
  const nextVersion = useStorage<Version | null>('kintsu-next-version', null, sessionStorage, {
    serializer: StorageSerializers.object,
  })

  const hasNextVersion = computed(
    () => !!nextVersion.value && !!currentVersion.value && nextVersion.value.timestamp > currentVersion.value.timestamp,
  )

  async function fetchVersion(): Promise<Version | undefined> {
    if (process.env.NODE_ENV === 'development') {
      return {
        timestamp: 0,
        hash: '',
      }
    }

    const controller = new AbortController()
    const abortTimeout = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await fetch(new URL('/meta.json', window.location.origin), { signal: controller.signal })

      if (!response.ok) {
        console.error(`HTTP error when fetching version: ${response.status}`)
        return undefined
      }

      return response.json()
    } finally {
      clearTimeout(abortTimeout)
    }
  }

  const poll = useTimeoutPoll(
    async () => {
      try {
        const version = await fetchVersion()
        if (!version) {
          return
        }

        if (!currentVersion.value) {
          currentVersion.value = version
          return
        }

        if (version !== currentVersion.value) {
          nextVersion.value = version
          poll.pause()
        }
      } catch (err) {
        console.error('Error when fetching version:', err)
      }
    },
    1000 * 60,
    {
      immediate: true,
    },
  )

  const visibility = useDocumentVisibility()

  watch(visibility, (current) => {
    if (current === 'visible') {
      poll.resume()
    } else {
      poll.pause()
    }
  })

  function updateVersion() {
    if (!hasNextVersion.value) {
      console.warn('No new version available!')
      return
    }

    currentVersion.value = nextVersion.value
    nextVersion.value = null
    window.location.replace(window.location.href)
  }

  return {
    currentVersion,
    nextVersion,
    hasNextVersion,
    poll,
    updateVersion,
  }
})
